import React, {Component} from 'react';
import {inject, observer} from 'mobx-react';
import {action, autorun, computed, observable} from 'mobx';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import MarkupColorPicker from './MarkupColorPicker';
import {MarkUpMenuTypes} from '../../../constants/enums';
import iconThickness from '../../../assets/images/studio/markup/icon-thickness';
import {thicknessMarkupPanel} from '../../../themes/colors';
import MarkupToolButton from './MarkupToolButton';
import FormattedMessageCapitalized from '../../../components/FormattedMessageCapitalized';
import MarkupSubMenuIcons from './MarkupSubMenuIcons';
import {ReactComponent as IconRectangle} from '../../../assets/images/studio/markup/icon-rectangle.svg';
import {ReactComponent as IconOval} from '../../../assets/images/studio/markup/icon-oval.svg';
import {ReactComponent as IconLine} from '../../../assets/images/studio/markup/icon-line.svg';
import {ReactComponent as ArrowLeft} from "../../../assets/images/mobile-back.svg";
import debounce from 'lodash/debounce'
import {isDesktop} from "../../../utils/screenUtility";

const SCROLL_BY = 116;

@inject('whiteBoardStore', 'iconStore')
@observer
class MarkupSubMenu extends Component {
    @observable activeCategory;
    @observable hideRightArrow = true
    @observable hideLeftArrow = true

    @action componentDidMount() {
        const {type, iconStore} = this.props;
        switch (type) {
            case MarkUpMenuTypes.Symbols:
            case MarkUpMenuTypes.NotationText:
            case MarkUpMenuTypes.Stickers:
                this.activeCategory = iconStore.getCategoriesByType(type)[0]
                this.hideRightArrow = false
                break;
            default:
                return null;
        }

        autorun(() => {
            if (this.activeCategory && !this.isLoading) {
                // make sure toggle arrows happens after the render
                setTimeout(() => {
                    this.toggleHideArrows();
                })
            }
        })

    }

    @computed get isLoading() {
        return this.props.iconStore.isLoadingFirst(this.props.type, this.activeCategory)
    }

    @action changeActiveCategory = (categoryName) => {
        this.activeCategory = categoryName;
    }

    setRef = (node) => {
        this.scrollContainer = node
        if (isDesktop()) {
            this.scrollContainer?.addEventListener('wheel', this.handleScroll)
        }
    }

    markupShapes = [
        {
            type: 'rectangle',
            icon: <IconRectangle/>
        },
        {
            type: 'oval',
            icon: <IconOval/>
        },
        {
            type: 'line',
            icon: <IconLine/>
        },
    ]

    toggleHideArrows = debounce(action(() => {
        this.hideLeftArrow = this.scrollContainer?.scrollLeft === 0
        this.hideRightArrow = this.scrollContainer?.scrollLeft === (this.scrollContainer?.scrollWidth - this.scrollContainer?.offsetWidth)
    }), 100, {leading: true, trailing: true});

    handleScroll = (e) => {
        e.preventDefault()
        this.scrollContainer?.scrollBy(-e.wheelDeltaX + e.wheelDeltaY, 0);
        this.toggleHideArrows()
    }


    componentWillUnmount() {
        this.scrollContainer?.removeEventListener('wheel', this.handleScroll);
    }

    @action changeThickness = (thickness) => {
        this.props.changeThickness(thickness);
    };

    renderThickness = () => thicknessMarkupPanel.map((thickness, index) => {
        const {activeThickness} = this.props;
        const buttonClasses = classNames({
            [`sub-menu-item thickness-${thickness}`]: true,
            'active-thickness': activeThickness === thickness
        });

        return <MarkupToolButton
            key={index}
            buttonClass={buttonClasses}
            onClick={() => this.changeThickness(thickness)}
            icon={iconThickness}
        />;
    })

    renderCategories = (type) => {
        switch (type) {
            case MarkUpMenuTypes.Symbols:
            case MarkUpMenuTypes.Stickers:
            case MarkUpMenuTypes.NotationText:
                return this.props.iconStore.getCategoriesByType(type).map(category =>
                    <div className='markup-category'
                         key={category}
                         onClick={() => this.changeActiveCategory(category)}
                    ><FormattedMessageCapitalized
                        id={`markup.category.${category}`}
                        messageClasses={`text-category ${this.activeCategory === category ? 'active' : ''}`}/></div>)
            default:
                return <></>
        }
    }


    renderShapes = () => {
        const {addShape, activeShape} = this.props;
        return this.markupShapes.map((shape, index) => <MarkupToolButton
            key={index}
            buttonClass={'sub-menu-item shapes'}
            onClick={() => addShape(shape.type)}
            isActive={shape.type === activeShape}
            icon={shape.icon}
            iconViewBox="0 0 32 32"/>);
    }

    renderByType = (type) => {
        switch (type) {
            case MarkUpMenuTypes.Pen:
            case MarkUpMenuTypes.Highlighter:
                return this.renderThickness();
            case MarkUpMenuTypes.Shapes:
                return this.renderShapes();
            case MarkUpMenuTypes.Symbols:
            case MarkUpMenuTypes.NotationText:
            case MarkUpMenuTypes.Stickers:
                return <MarkupSubMenuIcons type={type} handleCanvasImage={this.props.handleCanvasImage} activeCategory={this.activeCategory}/>;
            default:
                return null;
        }
    }

    onRightClick = () => {
        this.scrollContainer.scrollBy(SCROLL_BY, 0);
        this.toggleHideArrows()
    }

    onLeftClick = () => {
        this.scrollContainer.scrollBy(-SCROLL_BY, 0);
        this.toggleHideArrows()
    }

    render() {
        const {showPicker, changeColor, type, activeColor} = this.props;

        const rightArrowClasses = classNames({
            "arrow": true,
            "hide": (this.hideRightArrow || this.isLoading)
        })

        const leftArrowClasses = classNames({
            "arrow left": true,
            "hide": (this.hideLeftArrow || this.isLoading)
        })

        return (
            <>
                <div className="center-upper" ref={this.setRef}>
                    <div className={leftArrowClasses} onClick={this.onLeftClick}>
                        <ArrowLeft/>
                    </div>

                    <div className='submenu-container'>
                        {this.renderByType(type)}
                    </div>
                    <div className={rightArrowClasses} onClick={this.onRightClick}>
                        <ArrowLeft/>
                    </div>
                </div>
                <div className="center-lower">
                    {!showPicker && this.renderCategories(type)}
                    {showPicker && <MarkupColorPicker activeColor={activeColor} changeColor={changeColor}/>}
                </div>
            </>
        );
    }
}

MarkupSubMenu.propTypes = {
    subOnClick: PropTypes.func,
    type: PropTypes.string,
    showPicker: PropTypes.bool,
};


export default MarkupSubMenu;
